/**
 * Sync — what is waiting, what went wrong, and what only a human can decide.
 *
 * The shell stores both sides of every version conflict and never picks a
 * winner. This screen is where that promise is kept: mine and theirs side by
 * side with enough context to choose, and the choice handed straight to
 * `resolveConflict`. Dead letters are a separate list, because "the server
 * refused this" is a different problem from "two devices disagree" and the
 * remedies are different too.
 */

import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { Link, useSyncState } from "../../main.tsx";
import type { ScreenProps } from "../../main.tsx";
import { Tile } from "../../components/Tile.tsx";
import { useOnline } from "../../components/useOnline.ts";
import {
  conflicts as listConflicts,
  deadLetters as listDeadLetters,
  outboxPaused,
  pauseOutbox,
  resolveConflict,
  resumeOutbox,
  retryDeadLetter,
  subscribeOutbox,
  type SidelinedOp,
} from "../../store/outbox.ts";
// `discardDeadLetter` deliberately from `sync`, not `outbox`: the one in `outbox`
// only drops the refused op, which leaves this device showing a value the server
// never took. The one here puts the server's row back first, which is what the
// confirmation dialog below already promises.
import { discardDeadLetter, retrySync, subscribeSync, syncNow } from "../../store/sync.ts";
import type { SyncStatus as SyncStatusValue } from "../../store/sync.ts";
import {
  describeConflict,
  describeDeadLetter,
  formatAgo,
  formatValue,
  type ConflictView,
  type DeadLetterView,
} from "./logic.ts";
import { Banner, Empty, Note, Section, SettingsScreen } from "./ui.tsx";

import "./settings.css";

/** Why the outbox was stopped by hand, as opposed to by an auth failure. */
const MANUAL_PAUSE = "Paused by you. Nothing is being sent; nothing is being lost.";

interface Sidelined {
  conflicts: SidelinedOp[];
  dead: SidelinedOp[];
  paused: boolean;
  reason: string | null;
}

const EMPTY: Sidelined = { conflicts: [], dead: [], paused: false, reason: null };

function useSidelined(): { view: Sidelined; reload: () => void } {
  const [view, setView] = useState<Sidelined>(EMPTY);

  const reload = useCallback(() => {
    void (async () => {
      try {
        const [c, d] = await Promise.all([listConflicts(), listDeadLetters()]);
        const paused = outboxPaused();
        setView({ conflicts: c, dead: d, paused: paused.paused, reason: paused.reason });
      } catch {
        // No IndexedDB (private mode, or the browser refused). The sync state
        // badge already says the app is degraded; an empty list is honest here.
        setView(EMPTY);
      }
    })();
  }, []);

  useEffect(() => {
    reload();
    const offOutbox = subscribeOutbox(reload);
    const offSync = subscribeSync(() => reload());
    return () => {
      offOutbox();
      offSync();
    };
  }, [reload]);

  return { view, reload };
}


function ConflictCard({
  view,
  onResolve,
}: {
  view: ConflictView;
  onResolve: (resolution: "keep_mine" | "take_theirs") => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const decide = (resolution: "keep_mine" | "take_theirs") => {
    setBusy(true);
    onResolve(resolution);
  };

  return (
    <Tile
      edge="ochre"
      title={`${view.entityLabel} · ${view.title}`}
      meta={`Edited here ${formatAgo(Date.now() - view.at)} · you had v${view.baseVersion}, the server has v${view.serverVersion ?? "?"}`}
    >
      <div className="pb-diff">
        <span className="pb-diff__head">Yours</span>
        <span className="pb-diff__head">On the server</span>
        {view.diffs.map((diff) => (
          <FragmentRow key={diff.field} label={diff.label} changed={diff.changed}>
            <span className="pb-diff__cell" data-side="mine" data-changed={String(diff.changed)}>
              {formatValue(diff.mine)}
            </span>
            <span className="pb-diff__cell" data-side="theirs" data-changed={String(diff.changed)}>
              {formatValue(diff.theirs)}
            </span>
          </FragmentRow>
        ))}
      </div>

      <Note>
        Keeping yours re-sends your change on top of the server&rsquo;s version. Taking theirs drops
        your change on this device and keeps what the server holds. Nothing else on the row is
        touched either way.
      </Note>

      <div className="pb-actions">
        <button
          type="button"
          className="pb-btn"
          data-variant="primary"
          disabled={busy}
          onClick={() => decide("keep_mine")}
        >
          Keep mine
        </button>
        <button type="button" className="pb-btn" disabled={busy} onClick={() => decide("take_theirs")}>
          Take theirs
        </button>
      </div>
    </Tile>
  );
}

/** The field caption spans both diff columns; the two cells sit under it. */
function FragmentRow({
  label,
  changed,
  children,
}: {
  label: string;
  changed: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <span className="pb-diff__field">
        {label}
        {changed ? "" : " · same on both"}
      </span>
      {children}
    </>
  );
}

function DeadLetterCard({
  view,
  failure,
  onRetry,
  onDiscard,
}: {
  view: DeadLetterView;
  /** Why the last discard of this row did not happen, or null. */
  failure: string | null;
  onRetry: () => void;
  onDiscard: () => void;
}): ReactNode {
  const [confirming, setConfirming] = useState(false);
  return (
    <Tile edge="rust" title={`${view.entityLabel} refused`} meta={formatAgo(Date.now() - view.at)}>
      <p className="pb-banner__body">{view.reason}</p>
      {/* On the card rather than at the top of the screen: the sentence is about
          this one row, and the header a few lines up is already counting it. */}
      {failure ? (
        <p className="pb-banner__body" role="alert">
          {failure}
        </p>
      ) : null}
      <ul className="pb-danger__list">
        {view.fields.map((field) => (
          <li key={field.label}>
            {field.label}: {field.value}
          </li>
        ))}
      </ul>
      {confirming ? (
        <Banner
          tone="danger"
          title="Discard this change?"
          actions={
            <>
              <button
                type="button"
                className="pb-btn pb-danger"
                data-variant="primary"
                onClick={onDiscard}
              >
                Discard for good
              </button>
              <button
                type="button"
                className="pb-btn"
                data-variant="quiet"
                onClick={() => setConfirming(false)}
              >
                Keep it
              </button>
            </>
          }
        >
          The edit above is thrown away and never reaches the server. Whatever the row said before
          this edit is what everyone keeps.
        </Banner>
      ) : (
        <div className="pb-actions">
          <button type="button" className="pb-btn" onClick={onRetry}>
            Try again
          </button>
          <button
            type="button"
            className="pb-btn"
            data-variant="quiet"
            onClick={() => setConfirming(true)}
          >
            Discard
          </button>
        </div>
      )}
    </Tile>
  );
}

/**
 * What is stuck, in one clause, when something below is waiting on a person.
 *
 * Written lower-case and capitalised at the point of use, because the same
 * clause has to read as a whole sentence at the top of the screen and as the
 * tail of the offline one.
 */
function stuckClause(refused: number, conflicts: number): string {
  const refusedPart =
    refused === 1 ? "the server refused one change" : `the server refused ${refused} changes`;
  const conflictPart =
    conflicts === 1
      ? "one change is waiting on you to pick a side"
      : `${conflicts} changes are waiting on you to pick a side`;
  if (refused > 0 && conflicts > 0) return `${refusedPart}, and ${conflictPart}.`;
  if (refused > 0) return `${refusedPart}. ${refused === 1 ? "It is" : "They are"} below.`;
  return `${conflictPart}.`;
}

/**
 * What to say when "Discard for good" could not be honoured.
 *
 * Discarding restores the server's own row before it drops the refused one, so a
 * discard that cannot reach the server refuses by design and leaves the dead
 * letter exactly where it was. Unhandled, that read as a dead button: the dialog
 * closed, the row stayed, and nothing on screen said why.
 *
 * The underlying error is not quoted. It is a transport failure by construction
 * — the only network call in that path is the one asking the server what the row
 * says — and `Load failed` on a settings screen names nothing anyone can act on.
 */
export function discardFailure(_err: unknown): string {
  return "Could not reach the server, so this change is still refused. It stays here until a discard gets through.";
}

/**
 * The one sentence at the top of this screen, and the only summary of sync a
 * person is likely to read.
 *
 * It used to say "Everything on this device has reached the server." with
 * "Refused (1)" printed directly beneath it — the queue was empty, so the ladder
 * fell through to the all-clear, and a write the server had already turned down
 * was not in the queue to be counted. Anything sidelined therefore outranks a
 * queue that is merely busy: a pending op clears itself, a refused or conflicted
 * one waits for a human and will wait forever if the header never mentions it.
 *
 * The offline line keeps its place at the top — being cut off explains the rest
 * — but not its promise that everything here "goes up when you are back", which
 * is not true of a change the server has already seen and refused.
 */
export function syncHeadline(input: {
  online: boolean;
  paused: boolean;
  pauseReason: string | null;
  status: SyncStatusValue;
  lastError: string | null;
  pending: number;
  refused: number;
  conflicts: number;
}): string {
  const stuck = stuckClause(input.refused, input.conflicts);
  const anythingStuck = input.refused + input.conflicts > 0;
  if (!input.online) {
    return anythingStuck
      ? `Offline. New ratings are kept here — but ${stuck}`
      : "Offline. Everything you rate is kept here and goes up when you are back.";
  }
  if (input.paused) return input.pauseReason ?? "Sending is paused.";
  if (input.status === "error") return input.lastError ?? "The last attempt failed.";
  if (anythingStuck) return `${stuck[0]?.toUpperCase() ?? ""}${stuck.slice(1)}`;
  if (input.pending > 0) return "Waiting to send.";
  return "Everything on this device has reached the server.";
}

export function SyncStatus(_props: ScreenProps): ReactNode {
  const sync = useSyncState();
  const online = useOnline();
  const { view, reload } = useSidelined();
  const [busy, setBusy] = useState(false);
  // One at a time: the dialog is modal in practice, and a second discard is only
  // reachable after the first has come back one way or the other.
  const [discardFailed, setDiscardFailed] = useState<{ opId: string; message: string } | null>(
    null,
  );

  const conflictViews = view.conflicts.map(describeConflict);
  const deadViews = view.dead.map(describeDeadLetter);

  // Counted off the same two lists the sections below render, so the header and
  // its own "Refused (1)" can never disagree about how many there are.
  const statusLine = syncHeadline({
    online,
    paused: view.paused,
    pauseReason: view.reason,
    status: sync.status,
    lastError: sync.lastError,
    pending: sync.pending,
    refused: deadViews.length,
    conflicts: conflictViews.length,
  });

  return (
    <SettingsScreen
      title="Sync"
      subtitle={statusLine}
      back={{ to: "/settings", label: "Settings" }}
    >
      <Section title="This device">
        <Tile sunk>
          <div className="pb-row">
            <span className="pb-label">Unsent changes</span>
            <span className="pb-badge" data-tone={sync.pending > 0 ? "pending" : "ok"}>
              {sync.pending}
            </span>
          </div>
          <div className="pb-row">
            <span className="pb-label">Last sync</span>
            <span className="pb-quiet">
              {sync.lastSyncAt === null ? "never" : formatAgo(Date.now() - sync.lastSyncAt)}
            </span>
          </div>
          <div className="pb-row">
            <span className="pb-label">Connection</span>
            <span className="pb-badge" data-tone={online ? "ok" : "error"}>
              {online ? "online" : "offline"}
            </span>
          </div>
          <div className="pb-row">
            <span className="pb-label">Sending</span>
            <span className="pb-badge" data-tone={view.paused ? "pending" : "ok"}>
              {view.paused ? "paused" : "on"}
            </span>
          </div>
        </Tile>

        <div className="pb-actions">
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            disabled={busy || sync.status === "syncing"}
            onClick={async () => {
              setBusy(true);
              try {
                // `retrySync` also lifts a pause, which is what someone tapping
                // "sync now" on a paused queue means.
                await retrySync();
              } finally {
                setBusy(false);
                reload();
              }
            }}
          >
            {sync.status === "syncing" ? "Syncing…" : "Sync now"}
          </button>
          {view.paused ? (
            <button
              type="button"
              className="pb-btn"
              onClick={() => {
                resumeOutbox();
                void syncNow();
              }}
            >
              Resume sending
            </button>
          ) : (
            <button
              type="button"
              className="pb-btn"
              data-variant="quiet"
              onClick={() => pauseOutbox(MANUAL_PAUSE)}
            >
              Pause sending
            </button>
          )}
        </div>

        <Note>
          Pausing stops changes travelling to the server. It never empties the queue — your ratings
          stay on this device and go up the moment you resume.
        </Note>
      </Section>

      <Section
        title={`Conflicts (${conflictViews.length})`}
        hint="Two devices changed the same thing. Both versions are here; the app will not pick for you."
      >
        {conflictViews.length === 0 ? (
          <Empty>Nothing to decide.</Empty>
        ) : (
          <div className="pb-stack">
            {conflictViews.map((conflict) => (
              <ConflictCard
                key={conflict.op_id}
                view={conflict}
                onResolve={(resolution) => {
                  void resolveConflict(conflict.op_id, resolution).then(reload);
                }}
              />
            ))}
          </div>
        )}
      </Section>

      <Section
        title={`Refused (${deadViews.length})`}
        hint="The server would not take these. They are kept here rather than dropped silently."
      >
        {deadViews.length === 0 ? (
          <Empty>Nothing refused.</Empty>
        ) : (
          <div className="pb-stack">
            {deadViews.map((dead) => (
              <DeadLetterCard
                key={dead.op_id}
                view={dead}
                failure={discardFailed?.opId === dead.op_id ? discardFailed.message : null}
                onRetry={() => void retryDeadLetter(dead.op_id).then(reload)}
                // A discard that cannot reach the server rejects on purpose and
                // keeps the row, so the refusal has to be caught and said rather
                // than left to the console. `reload` runs either way: the list is
                // what proves which of the two happened.
                onDiscard={() => {
                  setDiscardFailed(null);
                  void discardDeadLetter(dead.op_id)
                    .catch((err: unknown) =>
                      setDiscardFailed({ opId: dead.op_id, message: discardFailure(err) }),
                    )
                    .finally(reload);
                }}
              />
            ))}
          </div>
        )}
      </Section>

      <Note>
        Nothing here is a reason to reinstall the app.{" "}
        <Link to="/settings">Export a backup first</Link> if anything looks wrong — an export is the
        only copy that survives the browser reclaiming storage.
      </Note>
    </SettingsScreen>
  );
}
